import React, { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Activity, Download } from "lucide-react";
import type { GatewayAlarmNode as GatewayAlarmNodeType, Pathway } from "@/lib/pharmacyFirst/types";

export interface AlarmActionTaken {
  id: string;
  label: string;
  otherText?: string;
}

const ACTION_TAKEN_OPTIONS = [
  { id: "ae", label: "Referred to A&E" },
  { id: "999", label: "999 called" },
  { id: "gp", label: "Referred to GP" },
  { id: "mh-crisis", label: "Referred to mental health crisis team" },
  { id: "other", label: "Other" },
] as const;

interface GatewayAlarmNodeProps {
  node: GatewayAlarmNodeType;
  isDarkMode?: boolean;
  pathway?: Pathway;
  onAcknowledge: (actionTaken?: AlarmActionTaken) => void;
  onOpenCalculator?: (calculatorId: string) => void;
  onBack?: () => void;
  /** Opt-in refined stop screen (only for nodes that set `requireActionTaken`). */
  refined?: boolean;
  /** Red flags ticked on the source checklist, in checklist order. Absent = show every action line. */
  tickedFlagIds?: string[];
  flagNames?: Record<string, string>;
}

export function GatewayAlarmNode({
  node,
  isDarkMode = false,
  pathway,
  onAcknowledge,
  onOpenCalculator,
  onBack,
  refined = false,
  tickedFlagIds,
  flagNames,
}: GatewayAlarmNodeProps) {
  const [actionId, setActionId] = useState("");
  const [otherText, setOtherText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const patientLeafletsSection =
    node.leafletRefs && node.leafletRefs.length > 0 && pathway?.leaflets ? (
      <div
        className={`border rounded-lg p-4 space-y-2 ${
          isDarkMode ? "border-purple-700 bg-purple-950" : "border-purple-200 bg-purple-50"
        }`}
      >
        <h4
          className={`font-semibold text-sm ${
            isDarkMode ? "text-purple-200" : "text-purple-900"
          }`}
        >
          Patient information leaflets
        </h4>
        <div className="space-y-2">
          {node.leafletRefs.map((ref) => {
            const leaflet = pathway.leaflets[ref];
            if (!leaflet) return null;
            const isWelsh = leaflet.language === "cy";
            return (
              <a
                key={ref}
                href={leaflet.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 p-2 rounded text-sm font-medium transition ${
                  isDarkMode
                    ? "hover:bg-purple-900 text-purple-300"
                    : "hover:bg-purple-100 text-purple-700"
                }`}
              >
                <Download className="h-4 w-4" />
                <span>{leaflet.title}</span>
                {isWelsh && (
                  <span
                    className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isDarkMode
                        ? "bg-blue-900 text-blue-200 border border-blue-700"
                        : "bg-blue-100 text-blue-800 border border-blue-300"
                    }`}
                  >
                    Cymraeg
                  </span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    ) : null;

  if (refined && node.action === "close" && node.requireActionTaken) {
    const flagActions = node.flagActions;
    const orderedIds = tickedFlagIds
      ? [
          ...Object.keys(flagNames ?? {}).filter((id) => tickedFlagIds.includes(id)),
          ...tickedFlagIds.filter((id) => !(flagNames && id in flagNames)),
        ]
      : undefined;
    const complete = actionId !== "" && (actionId !== "other" || otherText.trim() !== "");
    const closeNow = () => {
      const chosen = ACTION_TAKEN_OPTIONS.find((o) => o.id === actionId);
      onAcknowledge({
        id: actionId,
        label: chosen?.label ?? actionId,
        ...(actionId === "other" ? { otherText: otherText.trim() } : {}),
      });
    };
    const muted = isDarkMode ? "text-gray-300" : "text-gray-700";
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg font-bold mt-2">{node.heading}</AlertTitle>
          <AlertDescription className="mt-2 text-base">
            {flagActions && orderedIds ? (
              <div className="space-y-3" data-testid="alarm-flag-actions">
                <p>{flagActions.intro}</p>
                {orderedIds.map((id) => (
                  <div key={id} data-testid={`alarm-flag-${id}`}>
                    <p className="font-semibold">{flagNames?.[id] ?? id}</p>
                    {(flagActions.flags.find((f) => f.id === id)?.lines ?? []).map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                ))}
                <p>{flagActions.outro}</p>
              </div>
            ) : (
              <span className="whitespace-pre-line">{node.body}</span>
            )}
          </AlertDescription>
        </Alert>

        {node.linkedCalculatorId && onOpenCalculator && (
          <Button variant="outline" size="lg" className="w-full" onClick={() => onOpenCalculator(node.linkedCalculatorId!)}>
            <Activity className="h-4 w-4 mr-2" />
            {node.linkedCalculatorLabel || "Open calculator"}
          </Button>
        )}

        {patientLeafletsSection}

        <fieldset className="space-y-2" data-testid="alarm-action-taken">
          <legend className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Action taken (required)
          </legend>
          <RadioGroup value={actionId} onValueChange={setActionId} aria-label="Action taken" className="space-y-2">
            {ACTION_TAKEN_OPTIONS.map((opt) => (
              <label
                key={opt.id}
                htmlFor={`alarm-action-${opt.id}`}
                className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition ${
                  actionId === opt.id
                    ? isDarkMode ? "border-blue-500 bg-blue-950" : "border-blue-500 bg-blue-50"
                    : isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <RadioGroupItem value={opt.id} id={`alarm-action-${opt.id}`} />
                <span className={`text-sm ${muted}`}>{opt.label}</span>
              </label>
            ))}
          </RadioGroup>
          {actionId === "other" && (
            <Textarea
              aria-label="Other action taken"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder="Describe the action taken"
              rows={2}
            />
          )}
        </fieldset>

        <FlowPrimaryButton
          label="Close Consultation"
          variant="destructive"
          size="lg"
          className="w-full"
          disabled={!complete}
          blockedReason="Record the action taken to close the consultation."
          testId="btn-close-consultation"
          onClick={() => (node.confirmBeforeClose ? setConfirmOpen(true) : closeNow())}
        />

        {node.confirmBeforeClose && (
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Close this consultation?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will end the consultation and cannot be undone. If a red flag
                  was selected by mistake, choose Back instead to return to the
                  checklist.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Go back</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={closeNow}
                >
                  Close consultation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {node.confirmBeforeClose && onBack && (
          <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
            Back to checklist
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle className="text-lg font-bold mt-2">{node.heading}</AlertTitle>
        <AlertDescription className="mt-2 text-base whitespace-pre-line">
          {node.body}
        </AlertDescription>
      </Alert>

      {node.linkedCalculatorId && onOpenCalculator && (
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => onOpenCalculator(node.linkedCalculatorId!)}
        >
          <Activity className="h-4 w-4 mr-2" />
          {node.linkedCalculatorLabel || "Open calculator"}
        </Button>
      )}

      {patientLeafletsSection}

      <div className="flex gap-2">
        {node.action === "close" && node.confirmBeforeClose ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="lg" className="w-full">
                Close Consultation
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Close this consultation?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will end the consultation and cannot be undone. If a red flag
                  was selected by mistake, choose Back instead to return to the
                  checklist.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Go back</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => onAcknowledge()}
                >
                  Close consultation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button
            onClick={() => onAcknowledge()}
            variant="destructive"
            size="lg"
            className="w-full"
          >
            {node.action === "reset" ? "Acknowledge & Continue" : "Close Consultation"}
          </Button>
        )}
      </div>

      {node.action === "close" && node.confirmBeforeClose && onBack && (
        <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
          Back to checklist
        </Button>
      )}
    </div>
  );
}
